import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Bell, Upload, PlusCircle, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import UploadDialog from "@/components/dialogs/UploadDialog";
import InputManualDialog from "@/components/dialogs/InputManualDialog";
import { useNavigate, useLocation } from "react-router-dom";

const PAGE_TITLES = {
  "/": { title: "RAMS Operational Dashboard – TRAMS", subtitle: "Real-time Reliability, Availability, Maintainability & Safety Operational Performance" },
  "/reliability": { title: "Reliability Analysis", subtitle: "MTBF trends and failure patterns across subsystems" },
  "/availability": { title: "Availability Analysis", subtitle: "System uptime and downtime performance" },
  "/maintainability": { title: "Maintainability Analysis", subtitle: "Recovery time (MTTR) breakdown and trends" },
  "/safety": { title: "Safety Analysis", subtitle: "Severity index and impact distribution" },
  "/pareto": { title: "Pareto Analysis", subtitle: "Prioritize subsystems by contribution to downtime" },
  "/trend": { title: "Trend Analysis", subtitle: "Failure events & downtime over time" },
  "/failure": { title: "Failure Analysis", subtitle: "Top failure modes and root causes" },
  "/subsystem": { title: "Subsystem Analysis", subtitle: "Breakdown by equipment and cause category" },
  "/events": { title: "Event Explorer", subtitle: "Full failure event log — search, filter & manage" },
  "/reports": { title: "Reports", subtitle: "Printable period summaries of RAMS performance" },
  "/settings": { title: "Settings", subtitle: "Operating hours, MTTR basis, KPI targets & user management" },
};

export default function Header() {
  const { user, isAdmin, logout } = useAuth();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const meta = PAGE_TITLES[pathname] || PAGE_TITLES["/"];

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header
      data-testid="app-header"
      className="h-20 ml-[260px] sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200 px-8 flex items-center justify-between trams-no-print"
    >
      <div>
        <h1 className="text-xl md:text-2xl font-black tracking-tight text-[#1e40af]">{meta.title}</h1>
        <p className="text-xs md:text-sm text-slate-500 mt-0.5">{meta.subtitle}</p>
      </div>

      <div className="flex items-center gap-3">
        {isAdmin && (
          <>
            <Button
              onClick={() => setUploadOpen(true)}
              data-testid="header-upload-btn"
              className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white h-9 text-xs font-semibold"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              Upload
            </Button>
            <Button
              onClick={() => setManualOpen(true)}
              data-testid="header-manual-btn"
              variant="outline"
              className="h-9 text-xs font-semibold border-slate-300"
            >
              <PlusCircle className="w-3.5 h-3.5 mr-1.5" />
              Input Manual
            </Button>
          </>
        )}

        <button className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid="header-notifications">
          <Bell className="w-4 h-4" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        </button>

        <div className="flex items-center gap-3 pl-3 border-l border-slate-200">
          <div className="text-right">
            <div className="text-sm font-semibold text-slate-900" data-testid="header-user-name">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500" data-testid="header-user-role">{user?.role}</div>
          </div>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1e40af] to-[#3b82f6] flex items-center justify-center text-white text-xs font-bold">
            {(user?.name || "U").split(" ").map(s => s[0]).slice(0,2).join("")}
          </div>
        </div>

        <Button
          onClick={doLogout}
          data-testid="header-logout-btn"
          variant="ghost"
          className="h-9 text-xs text-slate-600 hover:text-red-600 hover:bg-red-50"
        >
          <LogOut className="w-3.5 h-3.5 mr-1.5" />
          Logout
        </Button>
      </div>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />
      <InputManualDialog open={manualOpen} onOpenChange={setManualOpen} />
    </header>
  );
}
